import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { isAuxUser } from '@shared/constants/uuid';
import { Note, User } from '@shared/services/gql.service';
import { Utils } from '@shared/utils/utils';

export type NoteModalDataType = {
  notes: Note[];
  users: Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>>;
};

export type NoteModalResponseType = undefined | { note: string };

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InputComponent, FormsModule, ReactiveFormsModule, DatePipe],
  selector: 'aux-note-modal',
  template: `
    <div class="w-screen max-w-md">
      <div class="space-y-2.5">
        <aux-input
          label="Note"
          class="text-xs"
          [textArea]="true"
          resize="none"
          [rows]="2"
          [validators]="'required'"
          [formControl]="textarea"
          [showRequiredAsterisk]="true"
        />

        <div class="max-h-60 space-y-2.5 overflow-auto">
          @for (note of notes; track note) {
            <div class="border-l-4 border-aux-blue-dark pl-3">
              <div class="italic">
                {{ note.username }} - {{ note.create_date | date: 'dd MMMM y' }}:
              </div>
              <div>"{{ note.message }}"</div>
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class NoteModalComponent {
  notes: (Note & {
    username: string;
  })[] = [];
  ref = inject<CustomOverlayRef<NoteModalResponseType, NoteModalDataType>>(CustomOverlayRef);
  textarea = new FormControl('', Validators.required);

  close(bool: boolean) {
    const note = this.textarea.value;

    if (bool && !note) {
      this.textarea.markAsTouched();
      this.textarea.markAsDirty();
      this.textarea.updateValueAndValidity();

      return;
    }

    this.ref.close({
      note: note as string,
    });
  }

  constructor() {
    this.notes = (this.ref.data?.notes || []).map((note) => {
      const user = this.ref.data?.users.get(note.created_by);

      const username = isAuxUser(note.created_by)
        ? 'Auxilius'
        : `${user?.given_name} ${user?.family_name}`;

      return {
        ...note,
        message: Utils.unscrubUserInput(note.message),
        username,
      };
    });
  }
}
